import { CircularProgress } from "@mui/material";
import { get, toLower } from "lodash";
import queryString from "query-string";
import { useEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import ErrorMessage from "../../../common/js/components/ErrorMessage";
import useScrollToError from "../../../common/js/hooks/useScrollToError";
import Comments from "../comments/CommentsContainer";
import comments_constants from "../comments/constants";
import comments_operations from "../comments/ducks/operations";
import DocumentDrop from "../documents/DocumentDropContainer";
import searchOperations from "../search/ducks/operations";
import { findAndValidateField } from "../utils/findAndValidateField";
import utils from "../utils/index";
import utilOperations, { displaySellerLandlordCompData } from "../utils/operations";
import { documentDisabledStatuses } from "./constants";
import ConsultingDealContainer from "./ConsultingDealContainer";
import operations from "./ducks/operations";
import {
    installmentNonRequiredConstraints,
    runnerNonRequiredConstraints,
    splitModalNonRequiredConstraints,
    splitNonRequiredConstraints,
    voucherNonRequiredConstraints,
} from "./ducks/validation";
import VoucherForm from "./VoucherForm";
import VoucherToolbar from "./VoucherToolbar";

const VoucherContainer = (props) => {
    const {
        getVoucher,
        handleClearDealState,
        userProfile,
        voucher,
        voucherAccessAllowed,
        toggleComments,
        submitClicked,
        saveClicked,
        outsideModalSaveClicked,
        validateVoucherByField,
    } = props;

    const { id } = useParams();
    const location = useLocation();
    const isConsultingDeal = voucher.voucherTransactionType === "Consulting";
    const handleBlurValidate = (section, id, value) => {
        if (submitClicked || (outsideModalSaveClicked && section === "splitModalValidation")) {
            return validateVoucherByField(section, id, value, "submit");
        } else if (saveClicked) {
            return validateVoucherByField(section, id, value, "save");
        }
        const field = id && typeof id === "string" ? id.split(".").pop() : id;
        if (section === "installment") {
            return findAndValidateField(installmentNonRequiredConstraints, field, value);
        } else if (section === "splitValidation") {
            return findAndValidateField(splitNonRequiredConstraints, field, value);
        } else if (section === "runnerValidation") {
            return findAndValidateField(runnerNonRequiredConstraints, field, value);
        } else if (section === "splitModalValidation") {
            return findAndValidateField(splitModalNonRequiredConstraints, field, value);
        }
        return findAndValidateField(voucherNonRequiredConstraints, id, value);
    };

    useEffect(() => {
        const queryParams = queryString.parse(location.search);
        const comments = queryParams?.comments ?? false;
        if (comments) {
            toggleComments();
        }
        getVoucher(id);
        return () => {
            handleClearDealState();
        };
    }, []);

    const { containerRef: voucherRef } = useScrollToError({});

    return voucher.voucherTransactionType ? (
        voucherAccessAllowed ? (
            isConsultingDeal ? (
                <ConsultingDealContainer {...props} handleBlurValidate={handleBlurValidate} />
            ) : (
                <div ref={voucherRef}>
                    <div style={{ minWidth: "800px" }}>
                        <VoucherToolbar {...props} />
                        <DocumentDrop
                            entityKey={id}
                            type="voucher"
                            documentTypes={toLower(get(voucher, "voucherTransactionType", null))}
                            entityStatus={voucher.voucherStatus}
                            formDisabled={get(
                                documentDisabledStatuses,
                                `${userProfile.role}.${voucher.voucherStatus}`,
                                true
                            )}
                            errors={props.utilsErrors.documents}
                            parentType="VOUCHER"
                            stateCode={get(voucher.deal, "listing.property.primaryAddress.city.state.stateCode", null)}
                        />
                        <VoucherForm {...props} handleBlurValidate={handleBlurValidate} />
                        <div style={{ minWidth: "50%" }}>
                            <Comments
                                parentRecordType={comments_constants.VOUCHER_PARENT_RECORD_TYPE}
                                parentRecordId={id}
                            />
                        </div>
                    </div>
                </div>
            )
        ) : (
            <ErrorMessage message="You do not have permission to view this voucher" />
        )
    ) : (
        <div style={{ textAlign: "center", verticalAlign: "center" }}>
            <CircularProgress style={{ position: "absolute", top: "50%" }} color="primary" />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        userProfile: state.authReducer.userProfile,
        authenticatedUserProfile: state.authReducer.authenticatedUserProfile,
        valid: state.authReducer.validFormValues,
        snackbarOpen: state.authReducer.snackbarOpen,
        isSaving: state.utilsReducer.isSaving,
        saveError: state.voucherReducer.saveError ? state.voucherReducer.saveError : null,
        lastSaved: state.voucherReducer.lastSaved ? state.voucherReducer.lastSaved : null,
        brokerCommissions: state.voucherReducer.voucher.brokerCommissions,
        outsideSplits: state.voucherReducer.outsideSplits,
        outsideSplitModal: state.voucherReducer.outsideSplitModal,
        numComments: state.commentsReducer.comments.length,
        voucher: state.voucherReducer.voucher,
        deal: state.voucherReducer.voucher.deal,
        errors: state.voucherReducer.errors,
        submitClicked: state.voucherReducer.submitClicked,
        saveClicked: state.voucherReducer.saveClicked,
        outsideModalSaveClicked: state.voucherReducer.outsideSplitModal.outsideModalSaveClicked,
        utilsErrors: state.utilsReducer.errors,
        searchResults: state.utilsReducer.searchResults,
        autocompleteResults: state.searchReducer.autocompleteResults,
        snackbar: state.utilsReducer.snackbar,
        documentWarning: state.voucherReducer.documentWarning,
        confirmDialog: state.utilsReducer.confirmDialog,
        elementsSaving: state.utilsReducer.elementsSaving,
        savedDocuments: state.documentsReducer.savedDocuments,
        voucherAccessAllowed: utilOperations.voucherAccessAllowed(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.voucherReducer.voucher.brokerCommissions,
            state.voucherReducer.voucher
        ),
        displaySellerLandlordCompData: displaySellerLandlordCompData(
            state.authReducer.userProfile.user,
            state.authReducer.userProfile.role,
            state.authReducer.userProfile.personKey,
            state.dealReducer.brokersOnDeal,
            state.dealReducer.deal
        ),
        isStatusTrackerVisible: utilOperations.isStatusTrackerVisible(
            state.utilsReducer?.statusTracker?.isActive,
            state.authReducer.userProfile.role
        ),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getVoucher: (voucherKey) => {
            dispatch(operations.handleGetVoucher(voucherKey));
        },
        validateVoucherByField: (section, id, value, saveOrSubmit) => {
            return dispatch(operations.validateVoucherByField(section, id, value, saveOrSubmit));
        },
        handleSubmit: (save, callback, validateDocs = true) => {
            dispatch(utils.operations.toggleConfirm());
            return dispatch(
                operations.handleSubmit(save, callback, "submit", {
                    validateDocs: validateDocs,
                    validateForm: true,
                    validateNonRequired: false,
                })
            );
        },
        handleSave: () => {
            return dispatch(
                operations.handleSubmit(true, null, "saveAndContinue", {
                    validateDocs: false,
                    validateForm: true,
                    validateNonRequired: true,
                })
            );
        },
        handleSaveAndExit: () => {
            return dispatch(
                operations.handleSubmit(true, null, "saveAndExit", {
                    validateDocs: false,
                    validateForm: true,
                    validateNonRequired: true,
                })
            );
        },
        // Unused method, remove when time permits
        handleSubmitToReadyForPayment: (save, callback) => {
            dispatch(
                operations.handleSubmit(save, callback, "submit", {
                    validateDocs: true,
                    validateForm: true,
                    validateNonRequired: false,
                })
            );
        },
        toggleSnackBar: (open, variant, message) => {
            dispatch(utils.operations.snackbar(open, variant, message));
        },
        handleAutosaveNotSuccess: (isSaving, err) => {
            dispatch(operations.handleAutosaveNotSuccess(isSaving, err));
        },
        handleAutosaveSuccess: () => {
            dispatch(operations.handleAutosaveSuccess());
        },
        handleAddBrokerCommission: () => {
            dispatch(operations.handleAddBrokerCommission());
        },
        handleCreateOutsideSplitDraft: (enableFreeFormSearch, commissionType) => {
            dispatch(operations.createOutsideSplitDraft(enableFreeFormSearch, commissionType));
        },
        handleSaveOutsideSplit: (key, split) => {
            dispatch(operations.saveOutsideSplit(key, split));
        },
        handleDeleteOutsideSplit: (splitKey) => {
            dispatch(operations.deleteOutsideSplit(splitKey));
        },
        toggleOutsideSplitModal: (options) => {
            dispatch(operations.toggleOutsideSplitModal(options));
        },
        handleAddRunner: (parent) => {
            dispatch(operations.handleAddRunner(parent));
        },
        handleRemoveCommissionOrRunner: (options) => {
            dispatch(operations.handleRemoveCommissionOrRunner(options.key, options.parent, options.outside));
        },
        updateSplit: (splitKey, field, value, source) => {
            dispatch(operations.updateSplit(splitKey, field, value, source));
        },
        updateRunner: (parent, key, field, value) => {
            dispatch(operations.updateRunner(parent, key, field, value));
        },
        toggleComments: () => {
            dispatch(comments_operations.toggleComments());
        },
        setInitialPaymentMethod: (value) => {
            dispatch(operations.voucherChange("paymentMethod", value));
        },
        handleVoucherChange: (event) => {
            dispatch(operations.voucherChange(event.target.id, event.target.value));
        },
        handleCheckboxClick: (name, event) => {
            dispatch(operations.handleInput("voucher." + name, event.target.checked));
        },
        handleVoucherChangeAndCalculateSplits: (km, event) => {
            dispatch(operations.voucherChangeAndCalculateSplits(event.target.id, event.target.value, km));
        },
        handleDeleteVoucher: (voucherKey) => {
            return dispatch(operations.deleteVoucher(voucherKey));
        },
        handleSearchPerson: (inputValue) => {
            dispatch(operations.searchPerson(inputValue));
        },
        handleSearchOutsideBroker: (inputValue) => {
            dispatch(searchOperations.searchOutsideBroker(inputValue));
        },
        handleSearchCompany: (inputValue, companyType) => {
            dispatch(searchOperations.searchCompany(inputValue, companyType));
        },
        handleSearchBroker: (inputValue) => {
            dispatch(searchOperations.autoCompleteBroker(inputValue));
        },
        handleDocumentWarning: (open, proceed, docs) => {
            dispatch(operations.handleDocumentWarning(open, proceed, docs));
        },
        handleConfirmWarning: async (callback) => {
            dispatch(operations.handleDocumentWarning(false, true, [], null));
            return dispatch(
                operations.handleSubmit(true, callback, "submit", {
                    validateDocs: false,
                    validateForm: true,
                    validateNonRequired: false,
                })
            );
        },
        toggleConfirm: (onSubmit) => {
            dispatch(utils.operations.toggleConfirm(onSubmit));
        },
        handleInput: (path, value) => {
            dispatch(operations.handleInput("voucher." + path, value));
        },
        handleReducerLevelInput: (path, value) => {
            dispatch(operations.handleInput(path, value));
        },
        handleClearDealState: () => {
            dispatch(
                operations.handleInput("voucher", {
                    deal: {},
                    brokerCommissions: {},
                })
            );
            dispatch(operations.clearErrors());
        },
        handleAddArrayElement: (path, obj) => {
            dispatch(operations.addArrayElement("voucher.deal." + path, obj));
        },
        handleDeleteArrayElement: (path, index) => {
            dispatch(operations.deleteArrayElement("voucher.deal." + path, index));
        },
        handleUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.handleInput("voucher." + arrayPath + "[" + index + "]." + field, value));
        },
        handleCalculateSplits: () => {
            dispatch(operations.handleCalculateSplits());
        },
        handleSearchKmBrokers: (input) => {
            dispatch(searchOperations.searchKMBrokers(input));
        },
        getStatusTrackerData: (entityKey, entityType, abortSignal) => {
            dispatch(utilOperations.fetchStatusTrackerData(entityKey, entityType, abortSignal));
        },
        clearStatusTracker: () => {
            dispatch(utilOperations.deactivateStatusTracker());
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VoucherContainer);
